// Drops third-party tracking calls from the payload the Elastic RUM agent sends
// to the APM. With default options the agent creates a span for every fetch/XHR
// and a `resource` span for every asset of the page, whatever the origin, and
// it opens an `http-request` transaction named after the URL when none is open.
// Our own calls made while that transaction is open become its spans, so spans
// are filtered first and the transaction is judged afterwards.

interface ApmSpan {
  name?: string;
  type?: string;
  context?: { http?: { url?: string } };
}

interface ApmTransaction {
  name?: string;
  type?: string;
  spans?: ApmSpan[];
  span_count?: { started?: number };
}

export interface ApmPayload {
  transactions?: ApmTransaction[];
  errors?: unknown[];
}

// Hostname suffixes: `a.b.example.com` and `example.com` match `example.com`.
const TRACKING_HOST_SUFFIXES = [
  'googletagmanager.com',
  'google-analytics.com',
  'analytics.google.com',
  'doubleclick.net',
  'googleadservices.com',
  'googlesyndication.com',
  'adservice.google.com',
  'adservice.google.com.br',
  'facebook.net',
  'hotjar.com',
  'hotjar.io',
  'clarity.ms',
  'bat.bing.com',
  'c.bing.com',
  'analytics.tiktok.com',
  'analytics-sg.tiktok.com',
  'posthog.com',
];

// Hosts that also serve non-tracking traffic, so the path decides. `exact`
// paths match with or without a query string; `prefixes` need a deeper path.
const TRACKING_PATH_RULES = [
  {
    // google.com and its country variants (google.com.br, google.de...)
    host: /^(www\.)?google\.[a-z]{2,3}(\.[a-z]{2})?$/,
    exact: [],
    prefixes: ['/ccm/', '/pagead/', '/ads/', '/rmkt/', '/measurement/'],
  },
  {
    host: /(^|\.)facebook\.com$/,
    exact: ['/tr'],
    prefixes: ['/tr/', '/privacy_sandbox/pixel/'],
  },
];

// A request name is `METHOD url`; the agent keeps the method as it was passed
// to XHR `open`, so the case varies.
const REQUEST_NAME = /^[A-Za-z]+ (\S+)/;

// The agent opens these itself around a user action or an HTTP call, so one
// that lost every span is pure noise. `page-load` and `route-change` stay.
// (`temporary` transactions never reach the payload: the agent drops them.)
const MANAGED_TYPES = new Set(['user-interaction', 'http-request']);

const parseUrl = (value: unknown): URL | undefined => {
  if (typeof value !== 'string' || !value) {
    return undefined;
  }

  try {
    return new URL(value);
  } catch {
    return undefined;
  }
};

const matchesSuffix = (hostname: string, suffix: string): boolean =>
  hostname === suffix || hostname.endsWith(`.${suffix}`);

// PostHog is configurable per environment. A host equal to the page host is a
// same-origin proxy and would hide our own calls, so it is not a tracking host.
const getPosthogHost = (): string | undefined => {
  // Read as a literal property so Next inlines it at build time.
  const host = parseUrl(process.env.NEXT_PUBLIC_POSTHOG_HOST)?.hostname;
  const normalized = host?.toLowerCase();

  if (
    normalized &&
    typeof window !== 'undefined' &&
    normalized === window.location.hostname.toLowerCase()
  ) {
    return undefined;
  }

  return normalized;
};

// `posthogHost` is resolved once per payload by the filter, so the env parse
// does not repeat for every span.
const matchesTracking = (
  url: unknown,
  posthogHost: string | undefined
): boolean => {
  const parsed = parseUrl(url);

  if (!parsed) {
    return false;
  }

  // `host.` and `host` are the same host.
  const hostname = parsed.hostname.toLowerCase().replace(/\.$/, '');

  if (
    TRACKING_HOST_SUFFIXES.some((suffix) => matchesSuffix(hostname, suffix))
  ) {
    return true;
  }

  if (hostname === posthogHost) {
    return true;
  }

  return TRACKING_PATH_RULES.some(
    (rule) =>
      rule.host.test(hostname) &&
      (rule.exact.includes(parsed.pathname) ||
        rule.prefixes.some((prefix) => parsed.pathname.startsWith(prefix)))
  );
};

export const isThirdPartyTrackingUrl = (url: unknown): boolean =>
  matchesTracking(url, getPosthogHost());

// `external.http` spans and `http-request` transactions are named `METHOD url`;
// `resource` spans may be named by the bare URL.
const urlFromName = (name: unknown): string | undefined => {
  if (typeof name !== 'string') {
    return undefined;
  }

  return REQUEST_NAME.exec(name)?.[1] ?? name;
};

const isTrackingSpan = (
  span: ApmSpan,
  posthogHost: string | undefined
): boolean =>
  matchesTracking(span.context?.http?.url, posthogHost) ||
  matchesTracking(urlFromName(span.name), posthogHost);

const isTrackingTransaction = (
  transaction: ApmTransaction,
  posthogHost: string | undefined
): boolean => matchesTracking(urlFromName(transaction.name), posthogHost);

// A span the agent made for one of our own HTTP calls. Long tasks, resource
// timing and user timing spans are not calls, so they cannot justify keeping a
// transaction that was opened by a tracking request.
const isOwnCallSpan = (span: ApmSpan): boolean =>
  typeof span.type === 'string' && span.type.startsWith('external');

const withSpans = (
  transaction: ApmTransaction,
  spans: ApmSpan[]
): ApmTransaction => ({
  ...transaction,
  spans,
  // The agent counts spans before the filter runs.
  ...(transaction.span_count && {
    span_count: { ...transaction.span_count, started: spans.length },
  }),
});

const filterTransaction = (
  transaction: ApmTransaction,
  posthogHost: string | undefined
): ApmTransaction | undefined => {
  const original = transaction.spans;

  if (!Array.isArray(original)) {
    return isTrackingTransaction(transaction, posthogHost)
      ? undefined
      : transaction;
  }

  const spans = original.filter((span) => !isTrackingSpan(span, posthogHost));

  if (isTrackingTransaction(transaction, posthogHost)) {
    // The agent opens this `http-request` transaction when a tracking call
    // starts with none open, and our own calls made meanwhile become its spans.
    // Keep it only for those, under a name that no longer carries the tracker.
    const ownCall = spans.find(isOwnCallSpan);

    return ownCall
      ? withSpans(
          { ...transaction, name: ownCall.name ?? transaction.name },
          spans
        )
      : undefined;
  }

  if (
    original.length > 0 &&
    spans.length === 0 &&
    MANAGED_TYPES.has(transaction.type ?? '')
  ) {
    return undefined;
  }

  return withSpans(transaction, spans);
};

// Registered with `apm.addFilter`. It never throws: on any failure the original
// payload goes through, so our own telemetry is never lost to a filter bug.
export const dropThirdPartyTracking = <T extends ApmPayload>(payload: T): T => {
  try {
    if (!Array.isArray(payload.transactions)) {
      return payload;
    }

    const posthogHost = getPosthogHost();
    const transactions = payload.transactions
      .map((transaction) => filterTransaction(transaction, posthogHost))
      .filter((transaction) => transaction !== undefined);

    return { ...payload, transactions } as T;
  } catch {
    return payload;
  }
};
